import {
  CogIcon,
  DrillIcon,
  GemIcon,
  GraduationCapIcon,
  HardHatIcon,
  LeafIcon,
  MountainIcon,
  PickaxeIcon,
  SearchIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Mining & Mineral Resources Fundamentals: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to mining and mineral resources can be transformative or overwhelming. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'What mining and mineral resources are and why they matter',
      'Foundations of mining science',
      'The role of minerals in society',
      'The mining industry',
      'The mineral resource life cycle',
      'Basic mining concepts',
      'The connection between extraction and modern life',
      'Essential terminology',
      'Foundations for further mining study',
    ],
    result:
      'Your students understand mining and mineral resources as a practical discipline that connects geology, engineering, society, and sustainability — not an impossible list of terms to memorize.',
    icon: PickaxeIcon,
  },
  {
    number: '2',
    title: 'Geology, Minerals & Ore Deposits',
    subtitle: 'Everything your students need to know about geological foundations and mineral deposits',
    context:
      'This is where many students struggle to build the scientific foundation. This volume gives you the materials to help them truly understand mining geology.',
    items: [
      'Minerals and their properties',
      'Rocks and the rock cycle',
      'Ore and gangue',
      'Major types of mineral deposits',
      'Geological structures',
      'Grade and tonnage',
      'Deposit formation',
      'Resource estimation',
      'Geological mapping',
      'The connection between geology and mining',
      '12 practical geology case studies',
    ],
    result: 'Your students learn the geological principles that underpin all mining operations.',
    icon: GemIcon,
  },
  {
    number: '3',
    title: 'Mineral Exploration & Resource Evaluation',
    subtitle: 'How to understand mineral exploration and resource assessment',
    context:
      'Mineral exploration is complex but essential to understand. This volume gives you everything needed so your students grasp the discovery and evaluation process clearly.',
    items: [
      'The mineral exploration process',
      'Sampling and analysis',
      'Geochemistry',
      'Geophysics',
      'Drilling concepts',
      'Grade interpretation',
      'Resource concepts',
      'Reserve concepts',
      'Uncertainty in estimation',
      'Economic factors',
      'Factors in resource evaluation',
      'From exploration to development',
      '15 worked exploration and evaluation examples',
    ],
    result:
      'Your students understand how mineral resources are discovered, evaluated, and brought into development.',
    icon: SearchIcon,
  },
  {
    number: '4',
    title: 'Surface Mining Methods & Operations',
    subtitle: 'How surface mining works and its operational procedures',
    context:
      'Surface mining is the most visible form of mining. This volume makes these methods clear and comprehensible.',
    items: [
      'Major surface mining methods',
      'Open-pit mining',
      'Strip mining',
      'Open-cast mining',
      'Quarrying',
      'Placer mining',
      'Mine preparation',
      'Drilling and blasting fundamentals',
      'Loading and hauling',
      'Ore and waste management',
      'Safety considerations',
      'Environmental aspects',
      '12 practical surface mining case studies',
    ],
    result:
      'Your students understand how surface mining operates and the multiple considerations in mining operations.',
    icon: MountainIcon,
  },
  {
    number: '5',
    title: 'Underground Mining Methods & Operations',
    subtitle: 'How underground mining works and its operational procedures',
    context:
      'Underground mining is complex but important to understand. This volume makes these methods clear and manageable.',
    items: [
      'Underground mine access and development',
      'Room-and-pillar mining',
      'Cut-and-fill mining',
      'Longwall mining',
      'Stoping methods',
      'Block caving',
      'Material handling systems',
      'Ground stability',
      'Ventilation systems',
      'Water management',
      'Underground mine safety',
      'Equipment and operations',
      '15 practical underground mining case studies',
    ],
    result:
      'Your students understand underground mining methods and the engineering challenges they present.',
    icon: DrillIcon,
  },
  {
    number: '6',
    title: 'Mineral Processing & Value Recovery',
    subtitle: 'How extracted material becomes valuable mineral products',
    context:
      'Mineral processing transforms raw ore into valuable products. This volume explains this critical step clearly.',
    items: [
      'Understanding mineral processing',
      'Comminution (crushing and grinding)',
      'Screening and classification',
      'Gravity separation',
      'Magnetic and electrostatic separation',
      'Flotation processes',
      'Leaching concepts',
      'Concentrates and recovery',
      'Grade optimization',
      'Tailings management',
      'Introduction to smelting and refining',
      'Environmental considerations in processing',
      '15 practical mineral processing case studies',
    ],
    result:
      'Your students understand how ore is transformed into mineral products and the technology involved.',
    icon: CogIcon,
  },
  {
    number: '7',
    title: 'Mine Safety, Risk Management & Occupational Health',
    subtitle: 'Teaching mining safety and responsible risk management',
    context:
      'Mine safety is critical and must be understood clearly. This volume helps you teach it responsibly.',
    items: [
      'Understanding mining safety',
      'Hazard identification',
      'Risk management',
      'Hierarchy of controls',
      'Occupational health',
      'Dust exposure and control',
      'Personal protective equipment',
      'Human factors',
      'Incident prevention',
      'Emergency preparedness',
      'Safety culture',
      'Learning from incidents',
      '12 practical safety and risk case studies',
    ],
    result:
      'Your students understand mining safety as a scientific discipline built on evidence and continuous improvement.',
    icon: HardHatIcon,
  },
  {
    number: '8',
    title: 'Environmental Management, Sustainability & Mine Closure',
    subtitle: 'Environmental stewardship throughout the mining life cycle',
    context:
      "Mining's environmental footprint is important to understand. This volume does exactly that with balance and evidence.",
    items: [
      'Environmental management throughout mining',
      'Water management',
      'Land management and rehabilitation',
      'Air quality and dust control',
      'Waste and tailings management',
      'Biodiversity and habitat',
      'Energy and climate considerations',
      'Circular economy in mining',
      'Mine closure planning',
      'Post-closure management',
      'Sustainability principles',
      'Long-term stewardship',
      'Environmental monitoring',
      '15 practical environmental management case studies',
    ],
    result:
      'Your students understand that mining operations must be managed responsibly with rigorous environmental stewardship.',
    icon: LeafIcon,
  },
  {
    number: '9',
    title: 'Teaching Mining & Mineral Resources: Classroom Excellence',
    subtitle: 'Your complete guide as a mining education professional',
    context:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of mining and mineral resources teaching.',
    items: [
      'Building foundations for teaching mining',
      'Sequencing mining concepts',
      'Explaining difficult concepts',
      'Addressing misconceptions about mining',
      'Mineral literacy in the classroom',
      'Real-world connections',
      'Classroom activities',
      'Questions and discussions',
      'Teaching safety and environmental responsibility',
      'Mining and sustainability',
      'Critical thinking about mining',
      'Case studies',
      'Classroom debates and discussions',
      'Assessing student understanding',
      'Planning lessons and learning sequences',
      '10 complete integrated mining projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
